// src/providers/CompanyCacheContext.tsx
'use client'

import CompanyService from '@/service/company.service'
import { ICompanyObjectType } from '@repo/shared/types/company.types'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

type CompanyCacheContextType = {
  companies: ICompanyObjectType[]
  loading: boolean
  error: Error | null
  refresh: () => Promise<void>
  getCompanyById: (id: string) => Promise<ICompanyObjectType | undefined>
}

const defaultValue: CompanyCacheContextType = {
  companies: [],
  loading: true,
  error: null,
  refresh: async () => {},
  getCompanyById: async (id: string) => undefined,
}
const CompanyCacheContext = createContext<CompanyCacheContextType>(defaultValue)

export const CompanyCacheProvider = ({ children }: { children: ReactNode }) => {
  const [companies, setCompanies] = useState<ICompanyObjectType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchCompanies = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await CompanyService.getCompanies()
      setCompanies(response.data)
    } catch (err: any) {
      setError(err)
      setCompanies([])
    } finally {
      setLoading(false)
    }
  }, [])

  const getCompanyById = useCallback(
    async (id: string): Promise<ICompanyObjectType | undefined> => {
      return companies.find((c) => c.id === id)
    },
    [companies],
  )

  return (
    <CompanyCacheContext.Provider
      value={{ companies, loading, error, refresh: fetchCompanies, getCompanyById }}
    >
      {children}
    </CompanyCacheContext.Provider>
  )
}

export const useCompanyCache = () => {
  const context = useContext(CompanyCacheContext)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (context.companies.length === 0 && !context.loading && !fetchedRef.current) {
      fetchedRef.current = true
      context.refresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.companies.length, context.loading])

  return context
}
